import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common';
import { PlanFeaturesScroll } from '@/presentation/components/plans/PlanFeaturesScroll';
import {
  PLAN_FEATURE_UI_GROUPS,
  TIER_DESCRIPTIONS,
  TIER_LABELS,
  type PlanCatalogItem,
} from '@/domain/types/planCatalog';

interface ViewPlanDetailModalProps {
  isOpen: boolean;
  plan: PlanCatalogItem | null;
  onClose: () => void;
}

const money = (n: number) =>
  `$${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

export const ViewPlanDetailModal: React.FC<ViewPlanDetailModalProps> = ({
  isOpen,
  plan,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen || !plan) return null;

  const enabledFeatures = plan.features.filter(
    (f) => f.enabled && PLAN_FEATURE_UI_GROUPS[f.key] !== 'conexion',
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-[1px]" onClick={onClose} />

        <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-gray-700">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-teal-950/40" />
            <div className="relative flex items-start justify-between gap-3 px-5 py-5 sm:px-6">
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wider text-teal-700 dark:text-teal-300">
                  {t('pages.billing.planDetail')}
                </p>
                <h3 className="mt-1 truncate text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  {plan.nombre}
                </h3>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex rounded-full bg-slate-900/5 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200">
                    {TIER_LABELS[plan.productTier]}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      plan.planActivo
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
                    }`}
                  >
                    {plan.planActivo
                      ? t('pages.plans.active')
                      : t('pages.plans.inactive')}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  {TIER_DESCRIPTIONS[plan.productTier]}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-900/5 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="max-h-[min(65vh,560px)] space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  {t('pages.plans.price')}
                </p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                  {money(plan.precio)}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  {t('pages.plans.consultorios')}
                </p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                  {plan.consultorios}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40 col-span-2 sm:col-span-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  {t('pages.plans.profesionales')}
                </p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                  {plan.profesionales}
                </p>
              </div>
            </div>

            <PlanFeaturesScroll
              title={t('pages.billing.planCapabilities', {
                count: enabledFeatures.length,
              })}
              items={enabledFeatures.map((f) => ({ key: f.key, label: f.label }))}
              emptyLabel={t('pages.billing.planNoCapabilities')}
            />

            {plan.communication ? (
              <div className="rounded-xl border border-slate-200 p-3 dark:border-gray-700">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('pages.billing.planCommunication')}
                </p>
                <ul className="grid grid-cols-1 gap-1.5 text-sm text-slate-700 dark:text-slate-300 sm:grid-cols-2">
                  <li>
                    Email:{' '}
                    <strong>{plan.communication.channelEmail ? t('common.yes') : t('common.no')}</strong>
                  </li>
                  <li>
                    Tracking email:{' '}
                    <strong>{plan.communication.emailTracking === 'full' ? 'completo' : 'envío'}</strong>
                  </li>
                </ul>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-gray-700 dark:bg-gray-900/50 sm:px-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
